import * as React from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { Instance } from "mobx-state-tree";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderLambdalabsMobx } from "../../../../mst/stores/mk8s.draft.provider.lambdalabs";
import { Mk8sDraftProviderLambdalabsNodePoolModel } from "../../../../mst/stores/mk8s.draft.provider.lambdalabs.nodepool";
import { homeLink, request } from "../../../../services/cpln";
import { DryRunAlert } from "../../dryRunAlert";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { MoreHorizontal } from "react-feather";
import { NGProviderBrowser } from "../../ngProviderBrowser";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { DetectedTags, useHasDetectedTags } from "../../../../components/detail/detectedTags";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  lambdalabs: Mk8sDraftProviderLambdalabsMobx;
  nodePool: Instance<typeof Mk8sDraftProviderLambdalabsNodePoolModel>;
  index: number;
}
const ProviderLambdalabsNodePoolRaw: React.FC<Props> = ({ mk8sDraft, lambdalabs: l, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [browserKey, setBrowserKey] = React.useState("");

  const hasDetectedTags = useHasDetectedTags({
    value: np.labels.items,
    detectValue: (labels, key) => labels.some((l) => l.firstValue === key),
    detectionMap: {
      "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
    },
  });

  const pre = `lambdalabs.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.lambdalabs.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.lambdalabs.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.lambdalabs.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.lambdalabs.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.lambdalabs.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.lambdalabs.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.lambdalabs.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!l.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}instanceType`}
            label={np.instanceType.label}
            value={np.instanceType.value}
            onChange={np.instanceType.setValue}
            required={np.instanceType.isRequired}
            error={np.instanceType.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("instanceType"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.instanceType.setValue}
              path={`spec.provider.lambdalabs.nodePools[${index}].instanceType`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}minSize`}
            label={np.minSize.label}
            value={np.minSize.value}
            onChange={np.minSize.setValue}
            required={np.minSize.isRequired}
            error={np.minSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.minSize.setValue}
              path={`spec.provider.lambdalabs.nodePools[${index}].minSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}maxSize`}
            label={np.maxSize.label}
            value={np.maxSize.value}
            onChange={np.maxSize.setValue}
            required={np.maxSize.isRequired}
            error={np.maxSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.maxSize.setValue}
              path={`spec.provider.lambdalabs.nodePools[${index}].maxSize`}
            />
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className={hasDetectedTags ? "mb-2" : "mb-8"}
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <DetectedTags
          className="mb-8"
          value={np.labels.items}
          detectValue={(labels, key) => labels.some((l) => l.firstValue === key)}
          detectionMap={{
            "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
          }}
          pluralLabel="Labels"
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            l.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
      {browserKey === "instanceType" ? (
        <NGProviderBrowser
          title="Browse Lambda Labs Instance Types"
          initialValue={np.instanceType.value}
          fetchData={async () => {
            // TODOMK8S paginate
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "live-instance-types",
                cluster: mk8sDraft.asObject,
              },
            });
            return Object.values(data.data).map((v: any) => ({
              ...v.instance_type,
              regions: v.regions_with_capacity_available || [],
            }));
          }}
          sortable
          onOk={(value) => np.instanceType.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "Cores", "Memory", "Storage", "GPUs", "Cost per Month"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the instance type is currently available in selected region (${mk8sDraft.provider_lambdalabs.region.value})`,
            },
          ]}
          rightAlign={["Cost per Month"]}
          distinctFilters={["Cores", "Memory", "Storage", "GPUs"]}
          getData={(item: any) => {
            const priceCentsPerDay = Number(item.price_cents_per_hour || 0) * 24;
            const priceCentsPerMonth = priceCentsPerDay * 30;
            const price = priceCentsPerMonth / 100;
            const cost = price.toLocaleString("en-US", { style: "currency", currency: "USD" });
            const available = item.regions.some((r) => r.name === mk8sDraft.provider_lambdalabs.region.value);

            return [
              //
              item.name,
              item.name,
              [available, available ? "Available" : "Not Available"],
              item.specs.vcpus,
              [item.specs.memory_gib, `${item.specs.memory_gib}Gi`],
              [item.specs.storage_gib, `${item.specs.storage_gib}Gi`],
              item.specs.gpus,
              [price, cost],
            ];
          }}
        />
      ) : null}
    </>
  );
};

export const ProviderLambdalabsNodePool = observer(ProviderLambdalabsNodePoolRaw);
